import React, { useRef, useState } from "react";
import axios from "axios";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import { NotificationManager } from "react-notifications";
import AppConfig from "constants/config";

const ACCEPTED_TYPES = ".csv,.xlsx,.xls";

function BulkUploadInventory(props) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setUploading(false);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    props.onClose();
  };

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    setFile(f || null);
    setResult(null);
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios({
        method: "get",
        url: `${AppConfig.baseUrl}inventory/inventoryBulkTemplate`,
        responseType: "blob",
        headers: { token: localStorage.getItem("admin_token") },
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", "Bulk_Inventory_Template.xlsx");
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      NotificationManager.error("Failed to download template");
    }
  };

  const upload = async () => {
    if (!file) {
      NotificationManager.warning("Please choose a CSV or XLSX file");
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(
        `${AppConfig.baseUrl}inventory/bulkUploadInventory`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            token: localStorage.getItem("admin_token"),
          },
        }
      );
      if (response.data && response.data.error) {
        NotificationManager.error(response.data.title || "Upload failed");
        setUploading(false);
        return;
      }
      setResult(response.data);
      if (response.data.summary && response.data.summary.created > 0) {
        NotificationManager.success(
          `${response.data.summary.created} inventory listing(s) created`
        );
        if (props.onUploaded) props.onUploaded();
      }
    } catch (err) {
      NotificationManager.error("Bulk upload failed");
    }
    setUploading(false);
  };

  const downloadErrorsCsv = () => {
    if (!result || !result.errors || result.errors.length === 0) return;
    // Keep "row" + all source columns; drop the redundant "name" column and put "error" last.
    const headers = new Set(["row"]);
    result.errors.forEach((e) => {
      if (e.source) Object.keys(e.source).forEach((k) => headers.add(k));
    });
    headers.add("error");
    const headerArr = Array.from(headers);
    const escape = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
    const lines = [headerArr.join(",")];
    result.errors.forEach((e) => {
      const row = headerArr.map((h) => {
        if (h === "row") return escape(e.row);
        if (h === "error") return escape(e.error);
        return escape(e.source ? e.source[h] : "");
      });
      lines.push(row.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", "Bulk_Inventory_Upload_Errors.csv");
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Bulk Upload Inventory</DialogTitle>
      <DialogContent>
        <div style={{ marginBottom: 16 }}>
          <Button variant="outlined" onClick={downloadTemplate}>
            Download Template (.xlsx)
          </Button>
          <span style={{ marginLeft: 12, color: "#666", fontSize: 13 }}>
            Headers marked with * are required. Each row names the seller and
            product. Max 2000 rows, 5 MB.
          </span>
        </div>

        <div
          style={{
            marginBottom: 16,
            padding: "10px 14px",
            backgroundColor: "#f5f7fa",
            border: "1px solid #e0e0e0",
            borderRadius: 6,
            fontSize: 13,
            color: "#333",
          }}
        >
          <strong>Allowed values for the <code>discount_type</code> column</strong>
          <span style={{ color: "#666" }}> - use exactly one of these (case-sensitive):</span>
          <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
            <li><strong>(leave blank)</strong> or <code>nodiscount</code> - No discount</li>
            <li><code>Discount</code> - % discount. Needs: <em>discount_per</em></li>
            <li><code>Same</code> - Buy X get Y free of the same product. Needs: <em>purchase, bonus</em></li>
            <li><code>SameAndDiscount</code> - Same offer + % discount. Needs: <em>purchase, bonus, discount_per</em></li>
            <li><code>Different</code> - Buy X get Y free of another product. Needs: <em>purchase, bonus, bonus_product_name, bonus_manufacturer</em></li>
            <li><code>DifferentAndDiscount</code> - Different offer + % discount. Needs: <em>purchase, bonus, discount_per, bonus_product_name, bonus_manufacturer</em></li>
          </ul>
        </div>

        <div style={{ marginBottom: 16 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            onChange={handleFileChange}
            disabled={uploading}
          />
          {file && (
            <span style={{ marginLeft: 12, color: "#333" }}>
              {file.name} ({Math.round(file.size / 1024)} KB)
            </span>
          )}
        </div>

        {uploading && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0" }}>
            <CircularProgress size={20} />
            <span>Uploading and validating…</span>
          </div>
        )}

        {result && result.summary && (
          <div style={{ marginTop: 8 }}>
            <div
              style={{
                display: "flex",
                gap: 24,
                padding: "10px 14px",
                backgroundColor: "#f5f5f5",
                borderRadius: 6,
                marginBottom: 12,
              }}
            >
              <div>
                <strong>{result.summary.total}</strong> row(s) processed
              </div>
              <div style={{ color: "#1b5e20" }}>
                <strong>{result.summary.created}</strong> created
              </div>
              <div style={{ color: "#b71c1c" }}>
                <strong>{result.summary.failed}</strong> failed
              </div>
            </div>

            {result.errors && result.errors.length > 0 && (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <strong>Failed rows</strong>
                  <Button size="small" variant="outlined" onClick={downloadErrorsCsv}>
                    Download errors CSV
                  </Button>
                </div>
                <div
                  style={{
                    maxHeight: 260,
                    overflowY: "auto",
                    border: "1px solid #e0e0e0",
                    borderRadius: 6,
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead style={{ backgroundColor: "#fafafa", position: "sticky", top: 0 }}>
                      <tr>
                        <th style={{ textAlign: "left", padding: "8px 10px", width: 70 }}>Row</th>
                        <th style={{ textAlign: "left", padding: "8px 10px", width: 220 }}>Product</th>
                        <th style={{ textAlign: "left", padding: "8px 10px" }}>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((e, idx) => (
                        <tr key={idx} style={{ borderTop: "1px solid #eee" }}>
                          <td style={{ padding: "6px 10px" }}>{e.row}</td>
                          <td style={{ padding: "6px 10px" }}>{e.name || "-"}</td>
                          <td style={{ padding: "6px 10px", color: "#b71c1c" }}>{e.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={uploading}>
          {result ? "Close" : "Cancel"}
        </Button>
        {!result && (
          <Button onClick={upload} color="primary" variant="contained" disabled={uploading || !file}>
            Upload
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default BulkUploadInventory;
